"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ChevronDown, ShieldCheck, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
  takeaway: string;
}
const faqData: FaqItem[] = [{
  id: "item-1",
  question: "What is income protection and how does it work?",
  answer: "Income protection (often structured as disability insurance) replaces a portion of your regular earned income if an injury, prolonged illness, or medical condition leaves you unable to perform your job duties. When a covered event occurs, the policy pays a steady monthly benefit directly to you so you can cover essential living costs like mortgage payments, utilities, groceries, and debt obligations without draining your retirement assets.",
  takeaway: "It acts as a personal paycheck replacement during recovery periods."
}, {
  id: "item-2",
  question: "How does income protection differ from standard workers' compensation?",
  answer: "Workers' compensation only covers injuries or medical illnesses that occur directly on the job or within the course of employment. Most disabling health conditions—such as serious illnesses, orthopedic injuries outside of work, or chronic health events—happen off the clock and are entirely excluded from workers' compensation. Income protection covers you 24 hours a day, regardless of where the illness or injury occurs.",
  takeaway: "Workers' comp is job-only; income protection covers illness and injury anywhere."
}, {
  id: "item-3",
  question: "What is the difference between short-term and long-term disability coverage?",
  answer: "Short-term disability policies typically begin paying benefits quickly (often within 1 to 14 days) and provide coverage for a brief duration, usually between 3 to 12 months. Long-term disability is built for severe or prolonged conditions, kicking in after an elimination period of 30, 90, or 180 days and paying benefits for 2 years, 5 years, 10 years, or up to retirement age (typically age 65 or 67).",
  takeaway: "Short-term covers immediate recovery; long-term defends against multi-year disruptions."
}, {
  id: "item-4",
  question: "How do elimination periods affect monthly premiums?",
  answer: "The elimination period is the waiting time between the onset of your disability and when monthly benefit checks begin. Choosing a longer elimination period (such as 90 days instead of 30 days) significantly lowers your ongoing policy premium because the insurer does not absorb short-duration claims. Aligning your waiting period with an existing emergency fund is a common strategy to minimize costs.",
  takeaway: "A longer waiting period lowers your premium if you have adequate emergency savings."
}, {
  id: "item-5",
  question: "Are income protection payouts subject to federal income tax?",
  answer: "Taxability depends on how premiums are paid. If you pay policy premiums with individual, after-tax dollars, the monthly benefits you receive during a disability claim are generally 100% income tax-free. If an employer pays the premium and deducts it as a business expense, the resulting benefit payments will typically count as taxable income.",
  takeaway: "Policies paid with after-tax personal funds generally yield tax-free monthly benefits."
}, {
  id: "item-6",
  question: "Can I keep my policy if I change employers or work independently?",
  answer: "Individual income protection policies belong to you, not your employer. If you change jobs, advance your career, or transition into full-time self-employment, your individual policy remains fully in force with the exact same coverage and premium structure, provided premium payments continue on schedule.",
  takeaway: "Individual policies are fully portable across career changes and independent practice."
}];
export default function IncomeProtection() {
  const [openId, setOpenId] = useState<string | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const toggleAccordion = (id: string) => {
    setOpenId(prev => prev === id ? null : id);
  };
  const animationProps = prefersReducedMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    whileInView: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 14
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      amount: 0.15
    },
    transition: {
      duration: 0.35,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="income-protection" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border transition-colors duration-200">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-6 md:px-8">
        {/* Centered Header Block */}
        <motion.div {...animationProps} className="mx-auto max-w-[840px] text-center mb-12 md:mb-16">
          <div className="flex items-center justify-center mb-4">
            <Badge variant="outline" className="bg-muted/70 text-foreground border-border px-3.5 py-1 text-xs font-semibold tracking-wider uppercase rounded-full">
              Income protection basics
            </Badge>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
            Safeguard your income when you cannot work
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto font-sans">
            Your ability to earn an income is your most valuable financial asset. Explore how individual coverage and waiting periods protect your household budget.
          </p>

          {/* Architectural Anchor Dot Rule Motif */}
          <div className="mt-8 flex items-center justify-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <div className="h-px w-10 bg-border" />
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <div className="h-px w-10 bg-border" />
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <div className="h-px w-10 bg-border" />
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
          </div>
        </motion.div>

        {/* Accordion List Container */}
        <div className="mx-auto max-w-[840px] space-y-3.5">
          {faqData.map((item, index) => {
          const isOpen = openId === item.id;
          return <motion.div key={item.id} data-index={index} {...animationProps} className="rounded-xl border border-border bg-card overflow-hidden transition-[border-color,box-shadow] duration-200 hover:border-primary/30 hover:shadow-xs">
                <h3>
                  <Button variant="ghost" type="button" onClick={() => toggleAccordion(item.id)} aria-expanded={isOpen} aria-controls={`content-${item.id}`} id={`trigger-${item.id}`} className="w-full flex items-center justify-between gap-4 p-5 sm:p-6 text-left cursor-pointer select-none group focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-auto hover:bg-transparent font-normal">
                    <span className="text-base sm:text-lg font-semibold text-card-foreground group-hover:text-primary transition-colors font-default">
                      {item.question}
                    </span>
                    <span className={`shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-muted/60 text-foreground transition-transform duration-280 ${isOpen ? "rotate-180 bg-secondary" : ""}`} aria-hidden="true">
                      <ChevronDown className="w-4 h-4" />
                    </span>
                  </Button>
                </h3>

                <AnimatePresence initial={false}>
                  {isOpen && <motion.div id={`content-${item.id}`} role="region" aria-labelledby={`trigger-${item.id}`} initial={prefersReducedMotion ? {
                opacity: 1,
                height: "auto"
              } : {
                opacity: 0,
                height: 0
              }} animate={prefersReducedMotion ? {
                opacity: 1,
                height: "auto"
              } : {
                opacity: 1,
                height: "auto"
              }} exit={prefersReducedMotion ? {
                opacity: 0,
                height: "auto"
              } : {
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.28,
                ease: [0.25, 1, 0.5, 1]
              }} className="overflow-hidden">
                      <div className="px-5 sm:px-6 pb-6 pt-1 border-t border-border/50 text-card-foreground">
                        <p className="text-sm sm:text-base leading-relaxed text-muted-foreground font-sans">
                          {item.answer}
                        </p>
                        <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-muted/50 p-3.5 text-xs sm:text-sm border border-border/60">
                          <ShieldCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                          <div>
                            <span className="font-semibold text-foreground">Key takeaway: </span>
                            <span className="text-muted-foreground">{item.takeaway}</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>

        {/* Bottom Educational Assistance Note */}
        <motion.div {...animationProps} className="mx-auto max-w-[840px] mt-12 p-6 rounded-xl border border-border bg-card/60 flex items-start sm:items-center gap-4 text-left">
          <div className="flex size-10 items-center justify-center rounded-lg bg-muted text-primary shrink-0">
            <HelpCircle className="size-5" />
          </div>
          <div>
            <h4 className="text-base font-semibold text-foreground font-default">
              Have specific policy or waiting period questions?
            </h4>
            <p className="mt-0.5 text-sm text-muted-foreground font-sans">
              Review your current coverage or discuss independent disability options with our team.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}